import React, { useState } from "react";
import { subscribeToNewsletter } from "../api";
import { useLanguage } from "../context/LanguageContext";

export default function Newsletter() {
  const { language } = useLanguage();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [message, setMessage] = useState("");

  const t = {
    en: {
      title: "Join the Newsletter",
      subtitle:
        "Get notified every time I publish a new article or project. No spam, just pure insights.",
      placeholder: "Enter your email address",
      button: "Subscribe Now",
      success: "Welcome to the squad! You're officially subscribed.",
      loading: "Subscribing...",
      error: "Something went wrong. Please try again.",
    },
    fr: {
      title: "Rejoindre la Newsletter",
      subtitle:
        "Soyez notifié chaque fois que je publie un nouvel article ou projet. Pas de spam, juste du contenu pur.",
      placeholder: "Entrez votre adresse e-mail",
      button: "S'abonner",
      success: "Bienvenue dans l'équipe ! Vous êtes officiellement abonné.",
      loading: "Abonnement en cours...",
      error: "Un problème est survenu. Veuillez réessayer.",
    },
  };

  const currentT = t[language] || t.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
      setMessage(currentT.success);
      setEmail("");
    } catch (error) {
      setStatus("error");
      setMessage(error.message || currentT.error);
    }
  };

  return (
    <section className="py-16 px-5 lg:px-16 bg-white">
      <div className="max-w-8xl mx-auto">
        <div className="relative overflow-hidden bg-[#131315] rounded-[3rem] p-10 lg:p-20 ">
          {/* Decorative Elements */}
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#3aa1ff]/20 rounded-full blur-[80px]"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[#3aa1ff]/10 rounded-full blur-[80px]"></div>

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="lg:max-w-xl text-center lg:text-left">
              <h2 className="text-4xl lg:text-5xl font-Poppins font-bold text-white mb-6">
                {currentT.title} <span className="text-[#3aa1ff]">.</span>
              </h2>
              <p className="text-gray-400 text-lg font-Inter leading-relaxed">
                {currentT.subtitle}
              </p>
            </div>

            <div className="w-full max-w-md">
              {status === "success" ? (
                <div className="bg-[#3aa1ff]/10 border border-[#3aa1ff]/20 p-6 rounded-3xl text-center animate-fade-in">
                  <div className="w-16 h-16 bg-[#3aa1ff] rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-[#3aa1ff] font-Poppins font-bold">
                    {message}
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-4 text-gray-500 text-xs hover:text-white transition-colors"
                  >
                    Subscribe another email
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={currentT.placeholder}
                      required
                      className="w-full bg-[#1a1a1c] border border-gray-800 text-white px-6 py-5 rounded-2xl font-Inter focus:outline-none focus:border-[#3aa1ff] transition-all"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full bg-[#3aa1ff] text-white font-Poppins font-bold py-5 rounded-2xl hover:bg-[#288ce6] transition-all shadow-lg shadow-[#3aa1ff]/20 disabled:opacity-50"
                  >
                    {status === "loading" ? currentT.loading : currentT.button}
                  </button>
                  {status === "error" && (
                    <p className="text-red-400 text-sm font-medium text-center mt-2 animate-shake">
                      {message}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
